// 문제 해결 — 조건을 정리해 실제로 쓸 수 있는 답을 찾는다. 정답은 모든 경우를 따져 계산한다.
//  · 회의 잡기: 모두 비는 가장 이른 시각     · 길 찾기: 가장 빠른 길 (경유·통제 조건)
//  · 조건 추리: 누가 어느 요일인가 (조건은 답이 하나가 될 만큼만)
//  · 예산 고르기: 예산 안에서 효과 합이 가장 큰 조합 — 오답에 '효과 큰 것부터 담기' 같은 흔한 실수
//  · 작업 순서: 동시에 할 수 있을 때 다 끝내는 가장 짧은 기간
import type { ChoiceQuestion } from './ChoiceDrill';
import { hasBatchim, int, makeRng, pick, shuffle, type Rng } from './rng';

export interface Graph { nodes: { id: string; x: number; y: number }[]; edges: { a: string; b: string; w: number; blocked?: boolean }[] }
export interface SolveQuestion extends ChoiceQuestion {
  kind: 'schedule' | 'route' | 'logic' | 'budget' | 'critical';
  ask: string;
  options: string[];
  lines: string[];
  table?: { head: string[]; rows: string[][] };
  graph?: Graph;
}

export const SOLVE_COUNT = 5;
const eun = (w: string) => `${w}${hasBatchim(w) ? '은' : '는'}`;
const wa = (w: string) => `${w}${hasBatchim(w) ? '과' : '와'}`;
type Made = Omit<SolveQuestion, 'answer' | 'n' | 'limitSec' | 'options'> & { right: string; wrong: string[] };

/** 정답과 겹치지 않는 오답 3개 (앞의 것부터) */
function threeWrong(right: string, cands: string[]): string[] | null {
  const out: string[] = [];
  for (const c of cands) if (c !== right && !out.includes(c)) out.push(c);
  return out.length >= 3 ? out.slice(0, 3) : null;
}

// ---- 회의 잡기 ----
const PEOPLE = ['민수', '지영', '현우', '수진', '태호'];
const time = (slot: number) => { const m = 9 * 60 + slot * 30; return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`; };
const SLOTS = 18; // 9:00~18:00, 30분 단위

function schedule(r: Rng, lv: number): Made | null {
  const n = lv <= 3 ? 3 : lv <= 7 ? 4 : 5;
  const len = lv <= 3 ? 2 : lv <= 7 ? pick(r, [2, 3]) : pick(r, [3, 4]);
  const busy = PEOPLE.slice(0, n).map(() => {
    const blocks: [number, number][] = [];
    for (let k = int(r, 1, 3); k > 0; k--) {
      const s = int(r, 0, SLOTS - 2), e = Math.min(SLOTS, s + int(r, 2, 4));
      if (!blocks.some(([a, b]) => s < b && a < e)) blocks.push([s, e]);
    }
    return blocks.sort((a, b) => a[0] - b[0]);
  });
  const room: [number, number] | null = lv >= 5 ? (() => { const s = int(r, 0, SLOTS - 3); return [s, s + 2] as [number, number]; })() : null;
  const lunch: [number, number] = [6, 8];
  const free = (s: number, skip = -1, useRoom = true) => s + len <= SLOTS
    && ![lunch, ...(useRoom && room ? [room] : [])].some(([a, b]) => s < b && a < s + len)
    && busy.every((bl, i) => i === skip || !bl.some(([a, b]) => s < b && a < s + len));
  const valid = Array.from({ length: SLOTS }, (_, s) => s).filter((s) => free(s));
  if (!valid.length) return null;
  const right = valid[0];
  const cands = [
    ...busy.map((_, i) => Array.from({ length: SLOTS }, (_, s) => s).find((s) => free(s, i))),
    room ? Array.from({ length: SLOTS }, (_, s) => s).find((s) => free(s, -1, false)) : undefined,
    valid[1], valid[valid.length - 1], 0, 6,
  ].filter((s): s is number => s !== undefined && s !== right);
  const wrong = threeWrong(time(right), shuffle(r, cands).map(time));
  if (!wrong) return null;
  return {
    kind: 'schedule', ask: `${len * 30}분짜리 회의를 모두가 참석하게 잡을 때 가장 이른 시작 시각은?`,
    lines: ['근무 시간 9:00~18:00, 30분 단위로 시작할 수 있다.', '점심시간 12:00~13:00에는 회의를 하지 않는다.', ...(room ? [`회의실은 ${time(room[0])}~${time(room[1])}에 쓸 수 없다.`] : [])],
    table: { head: ['이름', '일정이 있는 시간'], rows: PEOPLE.slice(0, n).map((p, i) => [p, busy[i].map(([a, b]) => `${time(a)}~${time(b)}`).join(', ')]) },
    right: time(right), wrong, steps: [`가장 이른 빈 시간: ${time(right)}~${time(right + len)}`, '한 사람이라도 겹치거나 점심·회의실 조건에 걸리면 안 된다'],
  };
}

// ---- 길 찾기 ----
const POS: Record<string, [number, number]> = { A: [0, 0], B: [1, 0], C: [2, 0], D: [0, 1], E: [1, 1], F: [2, 1] };
const LINKS: [string, string, number][] = [['A', 'B', 0.9], ['B', 'C', 0.9], ['D', 'E', 0.9], ['E', 'F', 0.9], ['A', 'D', 0.8], ['B', 'E', 0.8], ['C', 'F', 0.8], ['A', 'E', 0.35], ['B', 'D', 0.3], ['B', 'F', 0.35], ['C', 'E', 0.3]];

function paths(edges: Graph['edges'], from: string, to: string): { nodes: string[]; cost: number }[] {
  const out: { nodes: string[]; cost: number }[] = [];
  const walk = (at: string, seen: string[], cost: number) => {
    if (at === to) {
      out.push({ nodes: seen, cost });
      return;
    }
    for (const e of edges) {
      if (e.blocked) continue;
      const nx = e.a === at ? e.b : e.b === at ? e.a : null;
      if (nx && !seen.includes(nx)) walk(nx, [...seen, nx], cost + e.w);
    }
  };
  walk(from, [from], 0);
  return out;
}

function route(r: Rng, lv: number): Made | null {
  const edges = LINKS.filter(([, , p]) => r() < p).map(([a, b]) => ({ a, b, w: int(r, 3, 15) } as Graph['edges'][number]));
  let lead = '';
  if (lv >= 8 && edges.length > 6) {
    const e = pick(r, edges);
    e.blocked = true;
    lead = `${e.a}–${e.b} 도로는 공사로 막혔다(점선).`;
  }
  const via = lv >= 6 && r() < 0.5 ? pick(r, ['B', 'C', 'D', 'E']) : null;
  const all = paths(edges, 'A', 'F').filter((p) => !via || p.nodes.includes(via)).sort((x, y) => x.cost - y.cost);
  if (all.length < 3) return null;
  const best = all[0];
  if (all[1].cost === best.cost) return null; // 가장 빠른 길이 하나여야 풀이가 깔끔하다
  const fewest = [...all].sort((x, y) => x.nodes.length - y.nodes.length || x.cost - y.cost)[0];
  const wrong = threeWrong(`${best.cost}분`, [all[1].cost, fewest.cost, all[2].cost, all[all.length - 1].cost, best.cost + 2].map((c) => `${c}분`));
  if (!wrong) return null;
  return {
    kind: 'route', ask: `A에서 F까지 ${via ? `반드시 ${via}를 거쳐 ` : ''}가장 빨리 가는 데 걸리는 시간은?`,
    lines: ['선 위의 숫자는 걸리는 시간(분)이다.', ...(lead ? [lead] : [])],
    graph: { nodes: Object.entries(POS).map(([id, [x, y]]) => ({ id, x, y })), edges },
    right: `${best.cost}분`, wrong, steps: [`가장 빠른 길: ${best.nodes.join(' → ')} = ${best.cost}분`, `구간 수가 가장 적은 길(${fewest.nodes.join('→')})이 가장 빠르다는 보장은 없다`],
  };
}

// ---- 조건 추리 ----
const NAMES = ['가현', '나래', '다솔', '라온', '마루'];
const DAYS = ['월', '화', '수', '목', '금'];
type Clue = { text: string; ok: (a: number[]) => boolean };

function perms(n: number): number[][] {
  if (n === 1) return [[0]];
  return perms(n - 1).flatMap((p) => Array.from({ length: n }, (_, i) => [...p.slice(0, i), n - 1, ...p.slice(i)]));
}

function logic(r: Rng, lv: number): Made | null {
  const n = lv <= 3 ? 3 : lv <= 7 ? 4 : 5;
  const names = NAMES.slice(0, n), days = DAYS.slice(0, n);
  const sol = shuffle(r, Array.from({ length: n }, (_, i) => i)); // sol[사람] = 요일
  const all = perms(n);
  const makeClue = (): Clue | null => {
    const p = int(r, 0, n - 1), q = (p + int(r, 1, n - 1)) % n;
    switch (int(r, 0, 4)) {
      case 0: { const d = (sol[p] + int(r, 1, n - 1)) % n; return { text: `${eun(names[p])} ${days[d]}요일이 아니다.`, ok: (a) => a[p] !== d }; }
      case 1: return sol[p] < sol[q] ? { text: `${eun(names[p])} ${names[q]}보다 앞 요일이다.`, ok: (a) => a[p] < a[q] } : null;
      case 2: return Math.abs(sol[p] - sol[q]) === 1 ? { text: `${wa(names[p])} ${eun(names[q])} 이웃한 요일이다.`, ok: (a) => Math.abs(a[p] - a[q]) === 1 } : null;
      case 3: return Math.abs(sol[p] - sol[q]) > 1 ? { text: `${wa(names[p])} ${eun(names[q])} 이웃한 요일이 아니다.`, ok: (a) => Math.abs(a[p] - a[q]) > 1 } : null;
      default: { const d = (sol[p] + int(r, 1, n - 1)) % n; const [x, y] = [sol[p], d].sort(); return { text: `${eun(names[p])} ${days[x]}요일 또는 ${days[y]}요일이다.`, ok: (a) => a[p] === x || a[p] === y }; }
    }
  };
  const clues: Clue[] = [];
  let left = all;
  for (let guard = 0; left.length > 1 && guard < 200; guard++) {
    const c = makeClue();
    if (!c || clues.some((x) => x.text === c.text)) continue;
    const next = left.filter(c.ok);
    if (next.length === left.length) continue; // 아무것도 줄이지 못하는 조건은 버린다
    clues.push(c);
    left = next;
  }
  if (left.length !== 1) return null;
  // 없어도 답이 하나로 남는 조건은 뺀다 (최소한의 조건만)
  for (const c of shuffle(r, [...clues])) {
    const rest = clues.filter((x) => x !== c);
    if (all.filter((a) => rest.every((x) => x.ok(a))).length === 1) clues.splice(clues.indexOf(c), 1);
  }
  const d = int(r, 0, n - 1);
  const who = names[sol.indexOf(d)];
  return {
    kind: 'logic', ask: `${days[d]}요일에 일하는 사람은?`,
    lines: [`${names.join(', ')} ${n}명이 ${days[0]}~${days[n - 1]}요일에 하루씩, 서로 다른 요일에 일한다.`, ...clues.map((c) => `· ${c.text}`)],
    right: who, wrong: names.filter((x) => x !== who).slice(0, 3),
    steps: [`답: ${names.map((x, i) => `${x}=${days[sol[i]]}`).join(', ')}`, '요령: 가능한 요일을 사람마다 표로 적고, 조건으로 하나씩 지운다'],
  };
}

// ---- 예산 고르기 ----
const PROMO = ['전단지', 'SNS 광고', '체험단', '현수막', '지역 신문', '할인 쿠폰'];

function budget(r: Rng, lv: number): Made | null {
  const n = lv <= 3 ? 4 : lv <= 6 ? 5 : 6;
  const items = PROMO.slice(0, n).map((name) => ({ name, cost: int(r, 2, 12) * 10, gain: int(r, 5, 60) }));
  const total = items.reduce((s, x) => s + x.cost, 0);
  const cap = Math.round((total * (0.4 + r() * 0.2)) / 10) * 10;
  let best = { gain: -1, pick: [] as number[] };
  const values = new Set<number>();
  for (let mask = 0; mask < 1 << n; mask++) {
    const chosen = items.filter((_, i) => mask & (1 << i));
    if (chosen.reduce((s, x) => s + x.cost, 0) > cap) continue;
    const g = chosen.reduce((s, x) => s + x.gain, 0);
    values.add(g);
    if (g > best.gain) best = { gain: g, pick: items.flatMap((_, i) => (mask & (1 << i) ? [i] : [])) };
  }
  const greedy = (key: (x: typeof items[number]) => number) => {
    let money = cap, g = 0;
    for (const x of [...items].sort((a, b) => key(b) - key(a))) if (x.cost <= money) { money -= x.cost; g += x.gain; }
    return g;
  };
  const second = [...values].filter((v) => v < best.gain).sort((a, b) => b - a)[0];
  const wrong = threeWrong(`${best.gain}명`, [greedy((x) => x.gain), greedy((x) => x.gain / x.cost), second, Math.max(...items.filter((x) => x.cost <= cap).map((x) => x.gain)), best.gain + 5].filter((v) => v !== undefined).map((v) => `${v}명`));
  if (!wrong || best.gain <= 0) return null;
  return {
    kind: 'budget', ask: '예산 안에서 새 손님이 가장 많아지게 고를 때, 새 손님은 모두 몇 명인가?',
    lines: [`홍보 예산은 ${cap}만원이다. 같은 방법은 한 번만 쓸 수 있고, 효과는 그대로 더해진다.`],
    table: { head: ['홍보 방법', '비용(만원)', '예상 새 손님(명)'], rows: items.map((x) => [x.name, String(x.cost), String(x.gain)]) },
    right: `${best.gain}명`, wrong, steps: [`최선: ${best.pick.map((i) => items[i].name).join(' + ')} = ${best.gain}명`, '효과가 큰 것부터 담거나 가성비 순으로만 담으면 예산이 남거나 더 좋은 조합을 놓칠 수 있다'],
  };
}

// ---- 작업 순서 ----
function critical(r: Rng, lv: number): Made | null {
  const n = lv <= 4 ? 4 : lv <= 7 ? 5 : 6;
  const ids = 'ABCDEF'.slice(0, n).split('');
  const dur = ids.map(() => int(r, 1, 6));
  const pre = ids.map((_, i) => (i === 0 ? [] : shuffle(r, Array.from({ length: i }, (_, j) => j)).slice(0, int(r, i === 1 ? 1 : 0, Math.min(2, i)))));
  const finish: number[] = [];
  const from: number[] = [];
  ids.forEach((_, i) => {
    const start = Math.max(0, ...pre[i].map((j) => finish[j]));
    from[i] = pre[i].length ? pre[i].reduce((a, j) => (finish[j] > finish[a] ? j : a), pre[i][0]) : -1;
    finish[i] = start + dur[i];
  });
  const end = Math.max(...finish);
  const chain: string[] = [];
  for (let i = finish.indexOf(end); i >= 0; i = from[i]) chain.unshift(`${ids[i]}(${dur[i]})`);
  const sum = dur.reduce((a, b) => a + b, 0);
  if (sum === end) return null; // 모두 한 줄로 이어지면 동시 진행의 의미가 없다
  const wrong = threeWrong(`${end}일`, [sum, Math.max(...dur), end - 1, end + 1, end + 2].map((v) => `${v}일`));
  if (!wrong) return null;
  return {
    kind: 'critical', ask: '서로 관계없는 작업은 동시에 할 수 있을 때, 모든 작업을 끝내는 가장 짧은 기간은?',
    lines: ['앞 작업이 다 끝나야 시작할 수 있는 작업이 있다.'],
    table: { head: ['작업', '기간(일)', '먼저 끝나야 할 작업'], rows: ids.map((id, i) => [id, String(dur[i]), pre[i].length ? pre[i].map((j) => ids[j]).join(', ') : '없음']) },
    right: `${end}일`, wrong, steps: [`가장 긴 줄(핵심 경로): ${chain.join(' → ')} = ${end}일`, `전부 더한 ${sum}일은 동시에 못 할 때의 기간이다`],
  };
}

type Maker = (r: Rng, lv: number) => Made | null;
export function makeSolveRound(level: number, seed: number): SolveQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const plan: Maker[] = lv <= 2 ? [schedule, logic, budget, schedule, logic] : [schedule, logic, budget, route, critical];
  const limitSec = 100 + lv * 8;
  const out: SolveQuestion[] = [];
  for (const make of shuffle(rng, plan)) {
    for (let guard = 0; guard < 200; guard++) {
      const m = make(rng, lv);
      if (!m) continue;
      const options = shuffle(rng, [m.right, ...m.wrong]);
      const { right, wrong, ...rest } = m;
      void wrong;
      out.push({ ...rest, options, answer: options.indexOf(right), n: options.length, limitSec });
      break;
    }
  }
  return out;
}
